import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { fullName, time } from '../lib/data';

// Calendar view for appointments (gap 3.1). Week and month layouts over the
// already-filtered worklist rows; clicking an appointment opens the same peek
// drawer the list uses. Pure date math (no date lib) to stay dependency-light.

const DAY = 86400000;
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => new Date(startOfDay(d).getTime() + n * DAY);
const startOfWeek = (d) => addDays(d, -startOfDay(d).getDay()); // Sunday
const sameDay = (a, b) => startOfDay(a).getTime() === startOfDay(b).getTime();
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const riskDot = (level) => (level === 'high' ? 'bg-rose-500' : level === 'medium' ? 'bg-amber-500' : level === 'clear' || level === 'low' ? 'bg-emerald-500' : 'bg-slate-300');
const fmtRange = (a, b) => {
  const opt = { month: 'short', day: 'numeric' };
  const ya = a.getFullYear();
  return `${a.toLocaleDateString('en-US', opt)} – ${b.toLocaleDateString('en-US', { ...opt, year: 'numeric' })}`.replace(`, ${ya}`, ya === b.getFullYear() ? '' : '');
};

export default function AppointmentCalendar({ items, onSelect, initialDate }) {
  const indexed = useMemo(() => items.map((it, i) => ({ it, i })), [items]);
  const [mode, setMode] = useState('week');
  // Open on the busiest day so the calendar lands where the appointments are,
  // not on an earliest-but-sparse outlier week.
  const [anchor, setAnchor] = useState(() => {
    if (!items.length) return startOfDay(initialDate || new Date());
    const counts = {};
    let best = items[0].apt.startTime, bestN = 0;
    for (const it of items) {
      const k = startOfDay(it.apt.startTime).getTime();
      counts[k] = (counts[k] || 0) + 1;
      if (counts[k] > bestN) { bestN = counts[k]; best = it.apt.startTime; }
    }
    return startOfDay(best);
  });

  const byDay = (day) => indexed
    .filter(({ it }) => sameDay(it.apt.startTime, day))
    .sort((a, b) => new Date(a.it.apt.startTime) - new Date(b.it.apt.startTime));

  const step = (dir) => setAnchor((d) => addDays(d, dir * (mode === 'week' ? 7 : 30)));
  const today = () => setAnchor(startOfDay(new Date()));

  const weekStart = startOfWeek(anchor);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Month grid: full weeks covering the anchor's month.
  const monthFirst = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = startOfWeek(monthFirst);
  const monthDays = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const inMonth = (d) => d.getMonth() === anchor.getMonth();

  const ApptChip = ({ entry, compact }) => {
    const { it, i } = entry;
    return (
      <button
        onClick={() => onSelect(i)}
        className="w-full text-left rounded-md border border-slate-200 bg-white hover:border-brand-300 hover:bg-brand-50/50 transition-colors px-1.5 py-1 flex items-center gap-1.5 min-w-0"
        title={`${time(it.apt.startTime)} · ${fullName(it.patient)} · Dr. ${it.provider?.lastName}`}
      >
        <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${riskDot(it.risk.level)}`} />
        <span className="tnum text-[10px] text-slate-400 shrink-0">{time(it.apt.startTime)}</span>
        {!compact && <span className="text-[11px] text-slate-700 truncate">{fullName(it.patient)}</span>}
      </button>
    );
  };

  return (
    <div className="dh-card-flush p-4 space-y-3">
      {/* Calendar header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <button onClick={() => step(-1)} className="h-8 w-8 rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-slate-900 flex items-center justify-center"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => step(1)} className="h-8 w-8 rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-slate-900 flex items-center justify-center"><ChevronRight className="h-4 w-4" /></button>
          <button onClick={today} className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-600 hover:text-slate-900">Today</button>
          <div className="ml-2 text-sm font-semibold text-slate-900">
            {mode === 'week' ? fmtRange(weekStart, addDays(weekStart, 6)) : anchor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </div>
        </div>
        <div className="seg">
          <button className={mode === 'week' ? 'active' : ''} onClick={() => setMode('week')}>Week</button>
          <button className={mode === 'month' ? 'active' : ''} onClick={() => setMode('month')}>Month</button>
        </div>
      </div>

      {mode === 'week' ? (
        <div className="grid grid-cols-7 gap-2">
          {weekDays.map((d, i) => {
            const entries = byDay(d);
            const isToday = sameDay(d, new Date());
            return (
              <div key={i} className="min-h-[140px] rounded-lg border border-slate-100 bg-slate-50/40 p-1.5">
                <div className={`text-[11px] font-medium mb-1.5 px-0.5 flex items-center justify-between ${isToday ? 'text-brand-700' : 'text-slate-500'}`}>
                  <span>{DOW[d.getDay()]} {d.getDate()}</span>
                  {entries.length > 0 && <span className="tnum text-slate-400">{entries.length}</span>}
                </div>
                <div className="space-y-1">
                  {entries.map((e) => <ApptChip key={e.i} entry={e} />)}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-7 gap-2 mb-1">
            {DOW.map((d) => <div key={d} className="text-[11px] text-slate-400 font-medium text-center">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {monthDays.map((d, i) => {
              const entries = byDay(d);
              const isToday = sameDay(d, new Date());
              return (
                <div key={i} className={`min-h-[92px] rounded-lg border p-1.5 ${inMonth(d) ? 'border-slate-100 bg-white' : 'border-transparent bg-slate-50/40'}`}>
                  <div className={`text-[11px] mb-1 px-0.5 flex items-center justify-between ${isToday ? 'text-brand-700 font-semibold' : inMonth(d) ? 'text-slate-600' : 'text-slate-300'}`}>
                    <span>{d.getDate()}</span>
                    {entries.length > 0 && <span className="h-1.5 w-1.5 rounded-full bg-brand-400" />}
                  </div>
                  <div className="space-y-1">
                    {entries.slice(0, 2).map((e) => <ApptChip key={e.i} entry={e} compact />)}
                    {entries.length > 2 && <div className="text-[10px] text-slate-400 px-1">+{entries.length - 2} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {items.length === 0 && (
        <div className="text-center py-8 text-sm text-slate-400 flex flex-col items-center gap-2">
          <CalendarDays className="h-6 w-6" />No appointments match these filters.
        </div>
      )}
    </div>
  );
}
